import { Select } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';

import InputOption from '../../molecules/InputOption';

import CommonButton from '~atoms/CommonButton';
import PageTitle from '~atoms/PageTitle';
import { headerVars } from '~organisms/Header';

// Extracted to constant to satisfy sonarjs/no-duplicate-string linter rule
// This string literal appears 3 times in the file
const FLEX_START = 'flex-start';

export const PageGlobalStyles = createGlobalStyle(({ theme }) => ({
  body: {
    backgroundColor: theme.palette.metallicBlack,
  },
}));

export const GlobalSkeletonStyles = createGlobalStyle`
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const PageWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  // Break out of MainContent padding for full-bleed layout
  marginInline: `calc(-1 * ${headerVars.paddingInline})`,
  marginBlockStart: '-30rem',
  marginBlockEnd: '-100rem',
  width: `calc(100% + 2 * ${headerVars.paddingInline})`,
  height: `calc(100vh - ${headerVars.headerHeight})`,

  [theme.breakpoint.media.max.md]: {
    flexDirection: 'column',
    height: 'auto',
    minHeight: `calc(100vh - ${headerVars.headerHeight})`,
  },
}));

export const LeftColumn = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  fontSize: theme.typography.size16,
  paddingInline: `calc(${headerVars.paddingInline} + 77rem) calc(${headerVars.paddingInline} + 43rem)`,
  paddingBlock: '92rem',
  backgroundColor: theme.palette.metallicBlack,

  [theme.breakpoint.media.max.md]: {
    paddingInline: headerVars.paddingInline,
  },
}));

const SharedSectionTitle = styled(PageTitle)({
  fontWeight: 'normal',
  lineHeight: '32rem',
  letterSpacing: 0,
});

export const HeroTitle = styled(SharedSectionTitle)({
  marginBottom: '22rem',
});

export const HeroDescription = styled.span(({ theme }) => ({
  fontFamily: theme.typography.family.arial,
  fontSize: theme.typography.size16,
  color: theme.palette.artichoke,
  lineHeight: '18rem',
  marginBottom: '122rem',
  letterSpacing: 0,
  whiteSpace: 'pre-line',
}));

export const HeroImage = styled.img(({ theme }) => ({
  width: '50%',
  objectFit: 'cover',

  [theme.breakpoint.media.max.sm]: {
    display: 'none',
  },
}));

export const Title = styled(SharedSectionTitle)({
  marginBottom: '30rem',
});

export const ContentWrapper = styled.div({
  display: 'flex',
  gap: '20rem',
  alignItems: FLEX_START,
  position: 'relative',
});

export const RowsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

export const Row = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '20rem',
  alignItems: 'center',
  lineHeight: '32rem',
  color: theme.palette.white,
}));

export const RowTitle = styled.span(({ theme }) => ({
  minWidth: '128rem',
  textAlign: 'end',
  color: theme.palette.white,
  lineHeight: '18px',
  letterSpacing: 0,
}));

export const ButtonContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8rem',
  alignItems: FLEX_START,
  alignSelf: FLEX_START,
});

export const SaveEstimateButton = styled(CommonButton)(({ theme }) => ({
  fontSize: theme.typography.size20,
  position: 'absolute',
  top: '-4rem',
  left: '327px',
}));

export const CheckboxContainer = styled.div(({ theme }) => ({
  position: 'absolute',
  top: '48rem',
  left: '327px',
  color: theme.palette.white,
}));

export const CurrentDesignCheckbox = styled(InputOption)(({ theme }) => ({
  '#useCurrentDesign::before': {
    border: `1rem solid ${theme.palette.white}`,
    backgroundColor: theme.palette.metallicBlack,
  },

  '#useCurrentDesign:focus::before': {
    outline: `2rem solid ${theme.palette.white}`,
  },

  '#useCurrentDesign:focus:checked::before': {
    outline: `2rem solid ${theme.palette.black}`,
  },

  '#useCurrentDesign::after': {
    backgroundColor: theme.palette.metallicBlack,
    outline: `2rem solid ${theme.palette.white}`,
  },

  '#useCurrentDesign:checked::after': {
    backgroundColor: theme.palette.white,
    outline: 'none',
    backgroundImage: `url("data:image/svg+xml,<svg viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M3 7l3 3 5-5' stroke='${theme.palette.metallicBlack.replace('#', '%23')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
  },
}));

export const SelectList = styled(Select<string>)(({ theme }) => ({
  '& .ant-select-selector': {
    borderColor: `${theme.palette.white} !important`,
    backgroundColor: 'transparent !important',
  },
  '& .ant-select-selection-item': {
    fontSize: theme.typography.size16,
    fontFamily: theme.typography.family.arial,
    color: `${theme.palette.white} !important`,
  },
  '& .ant-select-arrow': {
    color: theme.palette.white,
  },
}));

export const SkeletonItem = styled.div(({ theme }) => ({
  '&[data-height="large"]': {
    height: '32rem',
  },
  '&[data-height="medium"]': {
    height: '16rem',
  },
  '&[data-width="small"]': {
    width: '120rem',
  },
  '&[data-width="medium"]': {
    width: '180rem',
  },
  '&[data-width="large"]': {
    width: '250rem',
  },
  backgroundColor: theme.palette.chineseWhite,
  borderRadius: '0.25rem',
  animation: 'pulse 1.5s ease-in-out infinite',
}));

export const SkeletonImage = styled.div(({ theme }) => ({
  width: '50%',
  height: '300rem',
  backgroundColor: theme.palette.chineseWhite,
  borderRadius: '8rem',
  animation: 'pulse 1.5s ease-in-out infinite',

  [theme.breakpoint.media.max.sm]: {
    display: 'none',
  },
}));

export const StoresListContainer = styled.div({
  marginTop: '32rem',
});
